import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { HTMLProps } from 'react';
import { classes } from 'shared';

export function EmailInput({
  email = '',
  error,
  onUpdate,
  onUpdateError,
  clean,
  className,
  iClassName,
  children,
  name = 'Email',
  spinner,
  ...props
}: {
  email: string | undefined;
  error: string | string[] | undefined;
  clean?: boolean;
  name?: string;
  spinner?: boolean;
  /** This modifies the container's classes */
  className?: string;
  iClassName?: string;
  children?: React.ReactNode;
  onUpdate: (v: string) => void;
  onUpdateError: (v: string | undefined) => void;
} & HTMLProps<HTMLInputElement>) {
  return (
    <div className={classes(className, 'relative mt-[8px]')}>
      <label
        htmlFor={'email'}
        className={classes(
          'absolute -top-2 left-2 z-[1] -mt-px inline-block select-none bg-white px-1 text-xs font-medium text-gray-900',
          (className?.includes('md:pl-2') || className?.includes('md:px-2')) && 'md:ml-2',
          props.disabled && 'bg-gradient-to-b from-white via-white to-gray-200',
          props.placeholder && 'hidden',
        )}>
        {name}
        {children}
      </label>
      <div className="relative">
        {spinner && (
          <div className="pointer-events-none absolute inset-y-0 right-3 z-[10] flex items-center pl-3">
            <span className="text-gray-500">
              <svg
                className="h-5 w-5 animate-spin text-green-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373
                    0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0
                    3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </span>
          </div>
        )}
        <input
          {...props}
          className={calculateClassName()}
          type="text"
          name={name}
          id={name}
          autoComplete={props.autoComplete || 'email'}
          value={email || ''}
          onChange={e => handleEmailChange(e.target.value)}
          onBlur={e => {
            validateEmail();
            if (props.onBlur) props.onBlur(e);
          }}
          aria-describedby="email-error"
        />
        {error && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
          </div>
        )}
      </div>
      <span id="email-error" className="text-sm text-red-600">
        {typeof error !== 'string' && error?.length ? error?.[0] : error}
      </span>
    </div>
  );

  function calculateClassName() {
    if (clean) return iClassName;
    return classes(
      iClassName,
      error
        ? 'border-red-300 pr-10 text-red-900 focus:border-red-500 focus:outline-none focus:ring-red-500'
        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
      'relative block w-full cursor-text rounded-md border bg-white px-4 py-3 pl-3 text-left placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 disabled:cursor-default disabled:bg-gray-200 disabled:text-gray-600',
      spinner !== undefined && 'pr-10',
    );
  }

  function handleEmailChange(v: string) {
    onUpdate(v);
    if (error) onUpdateError(undefined);
  }

  function validateEmail() {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const valid = re.test(String(email).toLowerCase());
    if (!valid && !!email) onUpdateError('Invalid email address');
    return valid;
  }
}
